/**
 * Persona types for neighbourhood insights
 * Simplified to 3 core personas that match backend API expectations
 */

export enum PersonaType {
  Family = 'family',
  Professional = 'professional',
  Students = 'student',
}

export interface PersonaConfig {
  type: PersonaType;
  label: string;
  description: string;
  priorities: {
    showPriceTrends: boolean;
    showTaxCalculator: boolean;
    showMarketInsights: boolean;
    showLocationScores: boolean;
    showAIInsights: boolean;
    highlightedScores?: string[]; // Which scores to emphasize (e.g., 'playground', 'transit')
  };
}

/**
 * Persona configurations defining what each persona sees
 * Simplified to 3 core personas aligned with backend API
 */
export const PERSONA_CONFIGS: Record<PersonaType, PersonaConfig> = {
  [PersonaType.Family]: {
    type: PersonaType.Family,
    label: 'Family',
    description: 'Focus on schools, safety, and family amenities',
    priorities: {
      showPriceTrends: false,
      showTaxCalculator: false,
      showMarketInsights: false,
      showLocationScores: true,
      showAIInsights: true,
      highlightedScores: ['quietness', 'transit', 'playground', 'education'],
    },
  },
  [PersonaType.Professional]: {
    type: PersonaType.Professional,
    label: 'Professional',
    description: 'Focus on commute, amenities, and lifestyle',
    priorities: {
      showPriceTrends: false,
      showTaxCalculator: false,
      showMarketInsights: false,
      showLocationScores: true,
      showAIInsights: true,
      highlightedScores: ['transit', 'lively', 'shopping'],
    },
  },
  [PersonaType.Students]: {
    type: PersonaType.Students,
    label: 'Student',
    description: 'Focus on affordability, transit, and nightlife',
    priorities: {
      showPriceTrends: false,
      showTaxCalculator: false,
      showMarketInsights: false,
      showLocationScores: true,
      showAIInsights: true,
      highlightedScores: ['transit', 'lively', 'affordability'],
    },
  },
};

/**
 * Get persona configuration
 */
export function getPersonaConfig(persona: PersonaType): PersonaConfig {
  return PERSONA_CONFIGS[persona];
}

/**
 * Check if a component should be shown for a persona
 */
export function shouldShowComponent(
  persona: PersonaType,
  component: keyof PersonaConfig['priorities'],
): boolean {
  const config = getPersonaConfig(persona);
  const value = config.priorities[component];

  // highlightedScores is string[], not boolean - skip it
  if (Array.isArray(value)) {
    return true;
  }

  return value ?? true;
}
